import { useEffect, useRef, useState } from "react";

import { Icon } from "./Icon";
import { PeoplePicker } from "./PeoplePicker";
import { api, type FeedbackAttachment, type FormFieldSchema } from "../services/api";

/**
 * Generic renderer for all form field types (Brief v1.2 §7.3), styled to the
 * "Online Forms" design prototype: sentence-case labels with required/optional
 * markers, selectable choice tiles for radio/checkbox, a pill toggle for the
 * single checkbox, a $-prefixed currency input, chevron selects, and a
 * drag-and-drop file zone. Classes are namespaced `iq-*` so this restyle only
 * affects the form runner + the admin builder preview (which both use this
 * component) and never the other `.form-field` forms (delegation, modals, etc).
 *
 * Keep this component dumb: it reads ``field`` + ``value`` and emits
 * ``onChange``. Validation + conditional-show logic live in the parent.
 *
 * File uploads reuse the existing feedback attachment endpoint per Hard Rules.
 */

export interface FormFieldRendererProps {
  field: FormFieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string | null;
  disabled?: boolean;
  /**
   * For `user_picker` fields: scope the people lookup to this company (iQuest
   * staff filing on behalf of, or admin previewing a client's form). Ignored
   * server-side for non-staff callers.
   */
  peopleCompanyId?: string | null;
}

export function FormFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled,
  peopleCompanyId,
}: FormFieldRendererProps) {
  if (field.type === "section_header") {
    return (
      <div className="form-section-header">
        <h3 className="form-section-title">{field.label}</h3>
        {field.help_text && <p className="form-section-blurb">{field.help_text}</p>}
      </div>
    );
  }

  // The single checkbox renders as a pill toggle that carries its own label,
  // so we suppress the standalone field label for it (matches the prototype).
  const isToggle = field.type === "single_checkbox";

  // Field width (design handoff §6.1): half-width fields pair up two-up inside
  // a `.rf-fieldgrid`; full-width (the default) spans both columns via `span2`.
  // Outside a grid the `span2` modifier is inert, so this is safe everywhere.
  const span = field.width === "half" ? "" : " span2";

  return (
    <div className={"iq-field" + span + (error ? " has-error" : "")}>
      {!isToggle && (
        <label className="iq-field-label" htmlFor={field.id}>
          {field.label}
          {field.required ? (
            <span className="iq-req">*</span>
          ) : (
            <span className="iq-opt">optional</span>
          )}
          {field.show_when && (
            <span className="rf-cond-flag">
              <Icon name="link" size={10} />
              Shown by a rule
            </span>
          )}
        </label>
      )}

      <FieldControl
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        peopleCompanyId={peopleCompanyId}
      />

      {field.help_text && <p className="iq-field-hint">{field.help_text}</p>}
      {error && (
        <div className="iq-field-error">
          <Icon name="alert" size={13} />
          {error}
        </div>
      )}
    </div>
  );
}

function FieldControl({
  field,
  value,
  onChange,
  disabled,
  peopleCompanyId,
}: Omit<FormFieldRendererProps, "error">) {
  switch (field.type) {
    case "long_text":
      return (
        <textarea
          id={field.id}
          className="iq-textarea"
          rows={5}
          value={(value as string | undefined) ?? ""}
          maxLength={field.max_length ?? undefined}
          placeholder={field.placeholder ?? undefined}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "number":
      if (field.is_currency) {
        return (
          <div className="iq-currency">
            <span className="iq-dollar">$</span>
            <input
              id={field.id}
              className="iq-input"
              inputMode="decimal"
              value={value === undefined || value === null ? "" : String(value)}
              placeholder={field.placeholder ?? "0"}
              disabled={disabled}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") onChange(null);
                else {
                  const num = Number(raw);
                  onChange(Number.isNaN(num) ? raw : num);
                }
              }}
            />
          </div>
        );
      }
      return (
        <input
          id={field.id}
          className="iq-input"
          type="number"
          step={field.decimal_places ? Math.pow(10, -field.decimal_places) : "any"}
          min={field.min_value ?? undefined}
          max={field.max_value ?? undefined}
          value={value === undefined || value === null ? "" : String(value)}
          placeholder={field.placeholder ?? undefined}
          disabled={disabled}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") onChange(null);
            else {
              const num = Number(raw);
              onChange(Number.isNaN(num) ? raw : num);
            }
          }}
        />
      );

    case "date":
    case "datetime":
      return (
        <input
          id={field.id}
          className="iq-input"
          type={field.type === "date" ? "date" : "datetime-local"}
          value={(value as string | undefined) ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );

    case "single_select":
      return (
        <div className="iq-select-wrap">
          <select
            id={field.id}
            className="iq-select"
            value={(value as string | undefined) ?? ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || null)}
          >
            <option value="" disabled>
              {field.placeholder ?? "Select…"}
            </option>
            {(field.options ?? []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <Icon name="chevDown" size={16} className="iq-chev" />
        </div>
      );

    case "multi_select": {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <MultiSelectControl
          id={field.id}
          placeholder={field.placeholder ?? "Select…"}
          options={field.options ?? []}
          value={arr}
          disabled={disabled}
          onChange={onChange}
        />
      );
    }

    case "checkbox_group": {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="iq-choice-group horiz">
          {(field.options ?? []).map((opt) => {
            const checked = arr.includes(opt.value);
            return (
              <button
                type="button"
                key={opt.value}
                className={"iq-choice sq" + (checked ? " sel" : "")}
                disabled={disabled}
                onClick={() =>
                  onChange(
                    checked ? arr.filter((v) => v !== opt.value) : [...arr, opt.value],
                  )
                }
              >
                <span className="iq-mark" />
                <span className="iq-ctxt">{opt.label}</span>
              </button>
            );
          })}
        </div>
      );
    }

    case "single_checkbox":
      return (
        <button
          type="button"
          id={field.id}
          className={"iq-toggle" + (value ? " on" : "")}
          disabled={disabled}
          aria-pressed={Boolean(value)}
          onClick={() => onChange(!value)}
        >
          <span className="iq-track">
            <span className="iq-knob" />
          </span>
          <span className="iq-toggle-label">{field.label}</span>
        </button>
      );

    case "radio_group": {
      const opts = field.options ?? [];
      const horiz = opts.length <= 3 && opts.every((o) => o.label.length <= 16);
      return (
        <div className={"iq-choice-group" + (horiz ? " horiz" : "")}>
          {opts.map((opt) => (
            <button
              type="button"
              key={opt.value}
              className={"iq-choice" + (value === opt.value ? " sel" : "")}
              disabled={disabled}
              onClick={() => onChange(opt.value)}
            >
              <span className="iq-mark" />
              <span className="iq-ctxt">{opt.label}</span>
            </button>
          ))}
        </div>
      );
    }

    case "file_upload":
      return (
        <FileUploadControl
          field={field}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(v) => onChange(v)}
          disabled={disabled}
        />
      );

    case "user_picker":
      return (
        <PeoplePicker
          id={field.id}
          value={(value as string | undefined) ?? null}
          onChange={(v) => onChange(v)}
          disabled={disabled}
          placeholder={field.placeholder ?? undefined}
          companyId={peopleCompanyId}
        />
      );

    case "short_text":
    default:
      return (
        <input
          id={field.id}
          className="iq-input"
          type="text"
          value={(value as string | undefined) ?? ""}
          maxLength={field.max_length ?? undefined}
          placeholder={field.placeholder ?? undefined}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

interface FileUploadControlProps {
  field: FormFieldSchema;
  value: string[];
  onChange: (urls: string[]) => void;
  disabled?: boolean;
}

function FileUploadControl({ field, value, onChange, disabled }: FileUploadControlProps) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setErr(null);
  }, [value.length]);

  const maxFiles = field.max_files ?? 1;
  const atLimit = value.length >= maxFiles;

  async function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setErr(null);
    try {
      const remaining = maxFiles - value.length;
      const picked = Array.from(files).slice(0, Math.max(0, remaining));
      const urls: string[] = [];
      for (const file of picked) {
        const uploaded: FeedbackAttachment = await api.feedback.uploadAttachment(file);
        urls.push(uploaded.url);
      }
      onChange([...value, ...urls]);
    } catch (caught) {
      setErr(caught instanceof Error ? caught.message : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  function remove(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <input
        ref={inputRef}
        id={field.id}
        type="file"
        style={{ display: "none" }}
        multiple={maxFiles > 1}
        accept={(field.accepted_mime_types ?? []).join(",") || undefined}
        disabled={disabled || uploading || atLimit}
        onChange={handlePick}
      />
      <div
        className="iq-filedrop"
        role="button"
        tabIndex={0}
        onClick={() => !disabled && !atLimit && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !disabled && !atLimit) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <Icon name="upload" size={22} style={{ marginBottom: 6, color: "var(--iq-text-muted)" }} />
        <div>
          {uploading ? (
            "Uploading…"
          ) : atLimit ? (
            "File limit reached"
          ) : (
            <>
              Drag files here, or <span className="link">browse</span>
            </>
          )}
        </div>
        <div style={{ fontSize: 12.5, marginTop: 4 }}>
          Up to {maxFiles} file{maxFiles > 1 ? "s" : ""}, {field.max_file_size_mb ?? 10} MB each.
        </div>
      </div>

      {err && <p className="iq-field-error" style={{ marginTop: 8 }}>{err}</p>}

      {value.map((url, i) => (
        <div className="iq-file-row" key={`${url}-${i}`}>
          <Icon name="file" size={16} style={{ color: "var(--iq-violet)" }} />
          <a href={url} target="_blank" rel="noreferrer" style={{ fontWeight: 500, color: "var(--iq-text)" }}>
            {decodeURIComponent(url.split("/").pop() ?? url)}
          </a>
          <button
            type="button"
            className="iq-fx"
            aria-label="Remove file"
            disabled={disabled}
            onClick={() => remove(i)}
          >
            <Icon name="x" size={15} />
          </button>
        </div>
      ))}
    </div>
  );
}

interface MultiSelectControlProps {
  id: string;
  placeholder: string;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

function MultiSelectControl({
  id,
  placeholder,
  options,
  value,
  onChange,
  disabled,
}: MultiSelectControlProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggle(optValue: string, checked: boolean) {
    if (checked) {
      if (!value.includes(optValue)) onChange([...value, optValue]);
    } else {
      onChange(value.filter((v) => v !== optValue));
    }
  }

  const labels = value.map((v) => options.find((o) => o.value === v)?.label ?? v).join(", ");

  return (
    <div className="form-multiselect" ref={wrapRef}>
      <button
        id={id}
        type="button"
        className="form-multiselect-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`form-multiselect-summary${value.length === 0 ? " is-empty" : ""}`}>
          {value.length === 0 ? placeholder : labels}
        </span>
        <span className="form-multiselect-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div className="form-multiselect-menu" role="listbox">
          {options.length === 0 ? (
            <div className="form-multiselect-empty">No options.</div>
          ) : (
            options.map((opt) => {
              const checked = value.includes(opt.value);
              return (
                <label key={opt.value} className="form-multiselect-option">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggle(opt.value, e.target.checked)}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ---- Conditional evaluator (shared between renderer and parent) ------------

import type { ConditionalExpression } from "../services/api";

/**
 * Evaluate a ConditionalExpression against the current form data. Mirrors the
 * backend ``_eval_condition`` so a field shown on the client is also accepted
 * on the server.
 */
export function evalCondition(
  expr: ConditionalExpression | null | undefined,
  data: Record<string, unknown>,
): boolean {
  if (!expr) return true;
  switch (expr.type) {
    case "and":
      return expr.children.every((c) => evalCondition(c, data));
    case "or":
      return expr.children.some((c) => evalCondition(c, data));
    case "not":
      return !evalCondition(expr.children[0], data);
    case "field_eq":
      return data[expr.field_id] === expr.value;
    case "field_in": {
      const v = data[expr.field_id];
      if (Array.isArray(v)) return v.some((item) => expr.values.includes(item));
      return expr.values.includes(v);
    }
    case "field_gt": {
      const v = Number(data[expr.field_id]);
      return Number.isFinite(v) && v > expr.value;
    }
    case "field_lt": {
      const v = Number(data[expr.field_id]);
      return Number.isFinite(v) && v < expr.value;
    }
    case "field_len_gt": {
      const v = data[expr.field_id];
      if (!Array.isArray(v)) return false;
      return v.length > Number(expr.value ?? 0);
    }
    case "field_len_lt": {
      const v = data[expr.field_id];
      if (!Array.isArray(v)) return false;
      return v.length < Number(expr.value ?? 0);
    }
    case "field_len_eq": {
      const v = data[expr.field_id];
      if (!Array.isArray(v)) return false;
      return v.length === Number(expr.value ?? 0);
    }
    default:
      return false;
  }
}
